import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { useMutation } from "@apollo/client";
import { Typography } from "@mui/material";
import { ConfirmDialog } from "@outpave/ds";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CreditCardAcceptIcon,
  CreditCardNotAcceptIcon,
  CreditCardValidationIcon,
} from "@hugeicons-pro/core-stroke-rounded";
import CardStatusType from "enums/CardStatusType";
import CardType from "enums/CardType";
import CardRequestType from "enums/CardRequestType";
import Role from "enums/Role";
import {
  APPROVE_CARD,
  DENY_CARD_REQUEST,
  GET_CARD_DETAIL,
  MY_CARDS,
} from "graphql/card";
import useGetLoggedInUser from "hooks/useGetLoggedInUser";
import { useAdminTools } from "hooks/useAdminTools";
import { useCheckUserRole } from "hooks/useCheckUserRole";
import { useCardData } from "hooks/useCardData";
import {
  canCancelCard,
  canEditCard,
  canFreezeCard,
} from "utils/cards/cardActionRules";
import LoadingModal from "components/common/LoadingModal";
import CardFormDrawer from "components/common/cards/CardFormDrawer";
import type { CardDetailCard } from "./CardDetails";
import {
  ActivateCardButton,
  ApproveCardButton,
  CancelCardButton,
  CardActionBar,
  DenyCardButton,
  EditCardButton,
  FreezeCardButton,
} from "./CardActionButtons";
import CancelCardDialog from "./CancelCardDialog";
import FreezeCardDialog from "./FreezeCardDialog";

export interface CardActionsContext {
  card: CardDetailCard;
  /** The drawer's own open state, for actions that reset their own sub-drawers. */
  open: boolean;
  /** Refetch the card and mark the list behind as stale. */
  refresh: () => void;
  /** Close the drawer, telling the list to refetch. */
  close: () => void;
  /** Hold the drawer open while a dialog's mutation is in flight. */
  setBusy: (busy: boolean) => void;
}

type Decision = "approve" | "deny";

const ConfirmIcon: React.FC<{ icon: typeof CreditCardAcceptIcon }> = ({
  icon,
}) => <HugeiconsIcon icon={icon} size={20} color="currentColor" />;

/**
 * What the viewer can do with a card, on every route. The cardholder's own
 * actions go to the cardholder alone; managing someone else's card is an admin
 * tool; approving or denying a request is whatever the server's flags allow.
 */
const CardActions: React.FC<CardActionsContext> = ({
  card,
  open,
  refresh,
  close,
  setBusy,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useGetLoggedInUser();
  const adminTools = useAdminTools();
  const isAuditor = useCheckUserRole(Role.Auditor, { exact: true });
  const isAccountant = useCheckUserRole(Role.Accountant, { exact: true });

  const cardId = card.id ?? null;
  const status = card.status ?? undefined;
  const request = card.pendingRequest;
  const hasRequest = !!request?.id;
  const isPolicyUpdate = request?.requestType === CardRequestType.POLICY_UPDATE;
  // The two request types this bar knows how to decide.
  const isDecidable =
    isPolicyUpdate || request?.requestType === CardRequestType.NEW;
  const isCardholder = !!user?.id && user.id === card.user?.id;
  // Auditors and accountants look at other people's cards; they do not run them.
  const manages = adminTools && !isCardholder && !isAuditor && !isAccountant;
  const canApprove = adminTools && isDecidable && !!request?.isApprovalAllowed;
  const canDeny = adminTools && isDecidable && !!request?.isDenyAllowed;

  // Approving a policy change sends its requested values, which this form holds.
  const review = useCardData({
    cardId: canApprove && isPolicyUpdate && cardId ? cardId : undefined,
  });

  const [editOpen, setEditOpen] = useState(false);
  const [freezeOpen, setFreezeOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);
  const [freezeBusy, setFreezeBusy] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [decision, setDecision] = useState<Decision | null>(null);
  const [deciding, setDeciding] = useState(false);

  useEffect(() => {
    setEditOpen(false);
    setFreezeOpen(false);
  }, [open, cardId]);

  useEffect(() => {
    setBusy(freezeBusy || cancelBusy || deciding);
    return () => setBusy(false);
  }, [freezeBusy, cancelBusy, deciding, setBusy]);

  const [approveNewCard] = useMutation(APPROVE_CARD);
  const [denyRequest] = useMutation(DENY_CARD_REQUEST);

  const decided = () => {
    setDecision(null);
    refresh();
    close();
  };

  const failed = (error: unknown) => {
    const message =
      (error as { graphQLErrors?: { message: string }[] })?.graphQLErrors?.[0]
        ?.message ?? (error as Error)?.message;
    toast.error(message || t("Please try again!"));
  };

  const approvePolicyUpdate = async () => {
    setDeciding(true);
    try {
      await review.handleApproveCardRequest();
      toast.success(t("Card request approved successfully!"));
      decided();
    } catch (error) {
      failed(error);
    } finally {
      setDeciding(false);
    }
  };

  const confirmDecision = async () => {
    if (decision === "approve" && isPolicyUpdate) {
      await approvePolicyUpdate();
      return;
    }
    setDeciding(true);
    try {
      if (decision === "approve") {
        await approveNewCard({ variables: { id: cardId } });
      } else {
        await denyRequest({
          variables: { id: cardId, cardRequestId: request?.id },
        });
      }
      decided();
    } catch (error) {
      failed(error);
      setDecision(null);
    } finally {
      setDeciding(false);
    }
  };

  const canActivate =
    isCardholder &&
    status === CardStatusType.ISSUED &&
    card.type === CardType.PHYSICAL;
  // An edit amends any open request and restarts its approvals, so it is never an approval in disguise.
  const showEdit = canEditCard(status) && (isCardholder || manages);
  // An open request is denied by its approver, not answered by cancelling the card.
  const showCancel =
    !!cardId &&
    canCancelCard(status, card.type) &&
    (isCardholder || (manages && !hasRequest));
  const showFreeze = canFreezeCard(status) && (isCardholder || manages);

  const emphasis = <Typography component="span" variant="subtitle2" />;
  const confirm =
    decision === "deny"
      ? isPolicyUpdate
        ? {
            title: t("Deny Card Limit Request"),
            messageKey: "You are about to Deny this card limit request",
          }
        : {
            title: t("Deny New Card Request"),
            messageKey: "You are about to Deny this new card request",
          }
      : isPolicyUpdate
        ? {
            title: t("Approve Card Limit"),
            messageKey: "You are about to Approve the card limit",
          }
        : {
            title: t("Approve New Card"),
            messageKey: "You are about to Approve this new card",
          };

  if (
    !(
      canActivate ||
      showEdit ||
      showCancel ||
      showFreeze ||
      canApprove ||
      canDeny
    )
  ) {
    return null;
  }

  return (
    <>
      <CardActionBar>
        {canActivate && (
          <ActivateCardButton
            onClick={() => {
              close();
              navigate("/my/activate");
            }}
          />
        )}
        {showEdit && <EditCardButton onClick={() => setEditOpen(true)} />}
        {showCancel && <CancelCardButton onClick={() => setCancelOpen(true)} />}
        {showFreeze && (
          <FreezeCardButton
            frozen={status === CardStatusType.FROZEN}
            onClick={() => setFreezeOpen(true)}
          />
        )}
        {canApprove && (
          <ApproveCardButton
            onClick={() => setDecision("approve")}
            disabled={
              isPolicyUpdate && review.specificLoadingStates.loadingCardData
            }
          />
        )}
        {canDeny && <DenyCardButton onClick={() => setDecision("deny")} />}
      </CardActionBar>

      <LoadingModal open={deciding} />

      <FreezeCardDialog
        open={freezeOpen}
        cardId={cardId}
        cardStatus={status}
        cardType={card.type ?? undefined}
        onClose={() => setFreezeOpen(false)}
        onCompleted={refresh}
        onBusyChange={setFreezeBusy}
      />

      <CancelCardDialog
        open={cancelOpen}
        cardId={cardId}
        onClose={() => setCancelOpen(false)}
        onCanceled={close}
        onBusyChange={setCancelBusy}
        refetchQueries={[GET_CARD_DETAIL, MY_CARDS]}
      />

      {cardId && (adminTools || isCardholder) && (
        <CardFormDrawer
          open={open && editOpen}
          cardId={cardId}
          onClose={(changed) => {
            setEditOpen(false);
            if (changed) refresh();
          }}
        />
      )}

      <ConfirmDialog
        open={decision !== null}
        title={confirm.title}
        message={
          <Trans
            i18nKey={confirm.messageKey}
            components={{
              freeze_text: emphasis,
              activate_text: emphasis,
              cancel_text: emphasis,
              deny_text: emphasis,
              activate_new_text: emphasis,
              activate_limit_text: emphasis,
            }}
          />
        }
        icon={
          <ConfirmIcon
            icon={
              decision === "deny"
                ? CreditCardNotAcceptIcon
                : isPolicyUpdate
                  ? CreditCardValidationIcon
                  : CreditCardAcceptIcon
            }
          />
        }
        tone={decision === "deny" ? "danger" : "primary"}
        confirmLabel={t("Yes")}
        cancelLabel={t("No")}
        busy={deciding}
        onConfirm={confirmDecision}
        onCancel={() => {
          if (!deciding) setDecision(null);
        }}
      />
    </>
  );
};

export default CardActions;
